import type { BillingMilestone } from '../types/agreement'
import { computeMilestoneAmounts, createEmptyMilestone } from './milestoneBilling'

export type MilestoneTemplateRow = {
  label: string
  percentage: number
}

export type BillingMilestoneTemplateOption = {
  id: string
  name: string
  description?: string | null
  milestones: MilestoneTemplateRow[]
  /** Built-in code presets use source 'builtin'; DB rows use 'saved'. */
  source: 'builtin' | 'saved'
  is_system?: boolean
}

/** Built-in presets always available in the picker (even before DB migration). */
export const BUILTIN_MILESTONE_PRESETS: BillingMilestoneTemplateOption[] = [
  {
    id: 'builtin-50-50',
    name: '50/50 Deposit & Final',
    description: 'Half at start, half on completion.',
    source: 'builtin',
    milestones: [
      { label: 'Deposit', percentage: 50 },
      { label: 'Final', percentage: 50 },
    ],
  },
  {
    id: 'builtin-30-70',
    name: '30/70 Mobilization / Final',
    description: 'Mobilization deposit then final payment.',
    source: 'builtin',
    milestones: [
      { label: 'Mobilization', percentage: 30 },
      { label: 'Final', percentage: 70 },
    ],
  },
  {
    id: 'builtin-40-40-20',
    name: '40/40/20 Three Phase',
    description: 'Start, midpoint, and closeout.',
    source: 'builtin',
    milestones: [
      { label: 'Start', percentage: 40 },
      { label: 'Midpoint', percentage: 40 },
      { label: 'Closeout', percentage: 20 },
    ],
  },
  {
    id: 'builtin-33-33-34',
    name: '33/33/34 Three Equal',
    description: 'Three roughly equal payments.',
    source: 'builtin',
    milestones: [
      { label: 'Payment 1', percentage: 33 },
      { label: 'Payment 2', percentage: 33 },
      { label: 'Payment 3', percentage: 34 },
    ],
  },
]

export function sumTemplatePercentages(rows: MilestoneTemplateRow[]): number {
  return rows.reduce((sum, r) => sum + Number(r.percentage || 0), 0)
}

export function isLockedMilestone(m: BillingMilestone): boolean {
  return m.status === 'invoiced' || m.status === 'paid'
}

/**
 * Apply a template's label/% rows as new pending milestones.
 * If any existing milestones are invoiced/paid, returns an error instead of mutating.
 */
export function applyMilestoneTemplate(
  rows: MilestoneTemplateRow[],
  totalAmount: number,
  existing: BillingMilestone[] = []
): { milestones: BillingMilestone[]; error?: string } {
  if (existing.some(isLockedMilestone)) {
    return {
      milestones: existing,
      error:
        'Cannot apply a template while some milestones are already invoiced or paid. Customize pending rows only, or wait until locked milestones are cleared.',
    }
  }

  const stamped: BillingMilestone[] = rows.map((row, index) => ({
    id: `tpl-${Date.now()}-${index}`,
    label: row.label || '',
    percentage: Number(row.percentage) || 0,
    amount: 0,
    sort_order: index,
    status: 'pending' as const,
  }))

  return {
    milestones: computeMilestoneAmounts(totalAmount, stamped),
  }
}

/** Blank customize path: one empty pending row. */
export function customizeMilestoneScheduleCompletely(
  totalAmount: number,
  existing: BillingMilestone[] = []
): { milestones: BillingMilestone[]; error?: string } {
  if (existing.some(isLockedMilestone)) {
    return {
      milestones: existing,
      error:
        'Cannot reset the schedule while some milestones are already invoiced or paid.',
    }
  }
  return {
    milestones: computeMilestoneAmounts(totalAmount, [createEmptyMilestone(0)]),
  }
}

/** Rows suitable for saving as a template (pending only, with labels). */
export function milestoneRowsForSave(milestones: BillingMilestone[]): MilestoneTemplateRow[] {
  return milestones
    .filter((m) => !isLockedMilestone(m) && (m.label || '').trim() !== '')
    .map((m) => ({
      label: m.label.trim(),
      percentage: Number(m.percentage) || 0,
    }))
}
